import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './advanced-filter-view.module.sass';
import { Button, Tooltip, Typography } from 'antd';
import cx from 'classnames';
import { DownOutlined } from '@ant-design/icons';
import moment from 'moment';

function getConditionText(groupedIndicators, condition, isTooltip) {
  let groupName = '';
  let fieldName = '';
  const operator = condition.operator;
  let value = condition.value;

  if (!groupedIndicators) {
    return;
  }
  if (condition?.type === 'date') {
    value = moment(condition.value).format('YYYY-MM-DD');
  }
  for (const gi of groupedIndicators) {
    const indicator = gi.indicators.find(
      (i) => i.dbAttribute === condition.indicator,
    );
    if (indicator) {
      groupName = gi.groupName;
      fieldName = indicator.indicatorName;
    }
    if (indicator?.indicatorName === 'Source Name') {
      let labelAsValue = [];
      for (const i in value) {
        labelAsValue.push(
          indicator?.options?.filter(
            (option) => value[i] === option?.value,
          )?.[0]?.label,
        );
      }
      value = labelAsValue;
    }
  }
  return isTooltip
    ? `${groupName} / ${fieldName}; ${operator}; ${value}`
    : `${fieldName} / ${operator}: ${
        value?.length > 50 ? value?.substring(0, 50) + '...' : value
      }`;
}

export function AdvancedFilterIndicators({
  filterProfile,
  activeTag,
  activeFeed,
}) {
  const { Text } = Typography;
  const groupedIndicators = useSelector(
    (state) => state?.ioc?.filterSettings?.groupedIndicators,
  );
  const feedState = useSelector((state) => state?.ioc?.feed_state);
  const [collapseFunction, setCollapseFunction] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const advancedFilterMenuOpened = useSelector(
    (state) => state.sidebar.advancedFilterMenuOpen,
  );
  const leftMenuOpened = useSelector(
    (state) => state.sidebar.leftMenuSideBarCollapsed,
  );

  useEffect(() => {
    setTimeout(() => {
      handleWindowResize();
    }, 300);
  }, [advancedFilterMenuOpened, leftMenuOpened]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    const containerHeight = document.getElementById('advancedFilterIndicators')
      ?.offsetHeight;

    //initiate collapse functionality on chips on load
    if (containerHeight > 92) {
      setCollapseFunction(true);
      setCollapsed(true);
    } else {
      setCollapseFunction(false);
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [activeFeed, activeTag, filterProfile]);

  const handleWindowResize = () => {
    const containerHeight = document.getElementById('advancedFilterIndicators')
      ?.offsetHeight;

    //initiate collapse functionality on chips during window resize
    if (containerHeight > 92) {
      setCollapseFunction(true);
      setCollapsed(true);
    } else {
      setCollapseFunction(false);
    }
  };

  const highlightCondition = (idx, cidx, open) => {
    const highlightedElement = document.querySelector(
      `[data-highlight-id="${idx}-${cidx}-condition"]`,
    );
    if (highlightedElement) {
      open
        ? highlightedElement.classList.add('highlightCondition')
        : highlightedElement.classList.remove('highlightCondition');
    }
  };

  return (
    <>
      <div
        id="advancedFilterIndicators"
        className={cx(styles.advancedFilterIndicators, {
          [styles.collapsable]: collapseFunction,
          [styles.expanded]: !collapsed,
        })}
      >
        {activeFeed?.name && (
          <div
            key={activeFeed.uuid + '-key'}
            className={styles.logicalExpression}
          >
            <Tooltip title={`Feed: ${activeFeed.name}`} placement="bottom">
              <div
                className={cx(styles.indicator, 'me-1')}
                onClick={() =>
                  document
                    .getElementsByClassName('highlightCondition')?.[0]
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              >
                <Text className={styles.conditionChipText}>
                  {`${feedState === 'Collection' ? 'Collection' : 'Feed'}: ${
                    activeFeed.name
                  }`}
                </Text>
              </div>
            </Tooltip>
          </div>
        )}

        {activeTag && (
          <div key={activeTag + '-key'} className={styles.logicalExpression}>
            <Tooltip
              title={`Tags / Tag name; Is; ${activeTag}`}
              placement="bottom"
            >
              <div
                className={cx(styles.indicator, 'me-1')}
                onClick={() =>
                  document
                    .getElementsByClassName('highlightCondition')?.[0]
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              >
                <Text className={styles.conditionChipText}>
                  {`Tag: ${activeTag}`}
                </Text>
              </div>
            </Tooltip>
          </div>
        )}

        {filterProfile?.condition_blocks &&
          filterProfile?.condition_blocks?.map((cb, idx) => {
            return (
              <div key={cb.uuid} className={styles.logicalExpression}>
                {cb.binary_operator && (
                  <Text strong className={styles.expressionBinaryOperator}>
                    {cb.binary_operator}
                  </Text>
                )}

                {cb.conditions?.map((condition, cidx) => {
                  return (
                    <>
                      {condition.binary_operator && (
                        <Text strong className={styles.conditionBinaryOperator}>
                          {condition.binary_operator}
                        </Text>
                      )}

                      <Tooltip
                        title={getConditionText(
                          groupedIndicators,
                          condition,
                          true,
                        )}
                        placement="bottom"
                        onOpenChange={(open) =>
                          highlightCondition(idx, cidx, open)
                        }
                      >
                        <div
                          className={styles.indicator}
                          data-cy="advancedFilterIndicator"
                          onClick={() =>
                            document
                              .getElementsByClassName('highlightCondition')?.[0]
                              ?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              })
                          }
                        >
                          <Text className={styles.conditionChipText}>
                            {getConditionText(
                              groupedIndicators,
                              condition,
                              false,
                            )}
                          </Text>
                        </div>
                      </Tooltip>
                    </>
                  );
                })}
              </div>
            );
          })}
      </div>

      {collapseFunction && (
        <Button
          className={cx(styles.expandButton, { [styles.expanded]: !collapsed })}
          size="small"
          onClick={() => setCollapsed(!collapsed)}
        >
          <DownOutlined /> &nbsp;Show {collapsed ? 'more' : 'less'}
        </Button>
      )}
    </>
  );
}
